/**
 * Actions sur un paquet, communes à l'arbre de la barre latérale et à l'écran des paquets :
 * menu « Plus d'actions », création, renommage, déplacement et suppression.
 */
import { useState, type ReactNode } from 'react';
import { ChampTexte } from '../composants/ChampTexte';
import { Dialogue } from '../composants/Dialogue';
import { Menu, type Ancre, type MenuElement } from '../composants/Menu';
import { useNotifier } from '../composants/Notifications';
import { naviguer } from '../routes';
import { pluriel } from '../outils';
import { construireArbre, aplatir, estDescendant, indexer } from './arbre';
import { useDonnees } from './contexte';
import { messageErreur } from './erreurs';

type DialogueOuvert =
  | { type: 'creer'; parentId: string | null }
  | { type: 'renommer'; id: string }
  | { type: 'supprimer'; id: string; cartes: number | null }
  | { type: 'ranger'; id: string; parentId: string };

export function useActionsPaquets() {
  const { depot, paquets, rechargerPaquets } = useDonnees();
  const notifier = useNotifier();
  const [menu, setMenu] = useState<{ id: string; ancre: Ancre } | null>(null);
  const [dialogue, setDialogue] = useState<DialogueOuvert | null>(null);
  const [nom, setNom] = useState('');
  const [erreurNom, setErreurNom] = useState<string | undefined>();

  const noeuds = construireArbre(paquets ?? []);
  const index = indexer(noeuds);

  async function executer(action: () => Promise<void>, succes?: string) {
    try {
      await action();
      await rechargerPaquets();
      if (succes) notifier({ message: succes, statut: 'succes' });
    } catch (e) {
      notifier({ message: messageErreur(e), statut: 'danger' });
    }
  }

  function deplacer(id: string, parentId: string | null) {
    const n = index.get(id);
    if (!n || n.paquet.parentId === parentId) return;
    if (n.paquet.parentId === null && parentId !== null) {
      setDialogue({ type: 'ranger', id, parentId });
      return;
    }
    const cible = parentId === null ? null : index.get(parentId);
    void executer(
      () => depot.deplacerPaquet(id, parentId),
      cible
        ? `${n.paquet.nom} rangé dans ${cible.paquet.nom}.`
        : `${n.paquet.nom} est un paquet principal.`,
    );
  }

  function ouvrirCreation(parentId: string | null) {
    setNom('');
    setErreurNom(undefined);
    setDialogue({ type: 'creer', parentId });
  }

  function elementsMenu(id: string): MenuElement[] {
    const n = index.get(id);
    if (!n) return [];
    const principal = n.paquet.parentId === null;
    const destinations: MenuElement[] = [
      ...(principal
        ? []
        : [
            {
              type: 'action' as const,
              libelle: 'Paquet principal',
              icone: 'layers' as const,
              description: 'En faire un paquet principal, avec ses propres réglages.',
              onChoisir: () => deplacer(id, null),
            },
            { type: 'separateur' as const },
          ]),
      ...aplatir(noeuds)
        .filter((d) => !estDescendant(index, d.paquet.id, id) && d.paquet.id !== n.paquet.parentId)
        .map((d): MenuElement => ({
          type: 'action',
          libelle: d.chemin.join(' › '),
          icone: d.niveau === 0 ? 'layers' : 'folder',
          onChoisir: () => deplacer(id, d.paquet.id),
        })),
    ];
    return [
      ...(principal
        ? [
            {
              type: 'action' as const,
              libelle: 'Lancer la séance',
              icone: 'play' as const,
              raccourci: 'Espace',
              onChoisir: () => naviguer({ ecran: 'seance', paquet: id }),
            },
          ]
        : []),
      {
        type: 'action',
        libelle: 'Nouvelle carte',
        icone: 'plus',
        raccourci: 'N',
        onChoisir: () => naviguer({ ecran: 'editeur', paquet: id }),
      },
      {
        type: 'action',
        libelle: 'Nouveau sous-paquet',
        icone: 'folder-plus',
        onChoisir: () => ouvrirCreation(id),
      },
      { type: 'separateur' },
      {
        type: 'action',
        libelle: 'Renommer',
        icone: 'pencil',
        onChoisir: () => {
          setNom(n.paquet.nom);
          setErreurNom(undefined);
          setDialogue({ type: 'renommer', id });
        },
      },
      ...(destinations.length > 0
        ? [
            {
              type: 'sous-menu' as const,
              libelle: 'Déplacer vers',
              icone: 'folder-open' as const,
              elements: destinations,
            },
          ]
        : []),
      ...(principal
        ? [
            {
              type: 'action' as const,
              libelle: 'Réglages du paquet',
              icone: 'settings' as const,
              onChoisir: () => naviguer({ ecran: 'reglages-paquet', paquet: id }),
            },
          ]
        : []),
      { type: 'separateur' },
      {
        type: 'action',
        libelle: 'Supprimer le paquet',
        icone: 'trash-2',
        danger: true,
        onChoisir: () => {
          setDialogue({ type: 'supprimer', id, cartes: null });
          void depot
            .cartesDuPaquet(id)
            .then((cartes) =>
              setDialogue((d) =>
                d?.type === 'supprimer' && d.id === id
                  ? { ...d, cartes: new Set(cartes.map((c) => c.carteMereId)).size }
                  : d,
              ),
            )
            .catch(() => undefined);
        },
      },
    ];
  }

  function validerNom(): string | null {
    const propre = nom.trim();
    if (propre === '') {
      setErreurNom('Saisissez un nom, par exemple Chapitre 5.');
      return null;
    }
    if (propre.length > 200) {
      setErreurNom('Le nom ne peut pas dépasser 200 caractères.');
      return null;
    }
    return propre;
  }

  let rendu: ReactNode = null;
  if (dialogue?.type === 'creer' || dialogue?.type === 'renommer') {
    const parent =
      dialogue.type === 'creer' && dialogue.parentId ? index.get(dialogue.parentId) : null;
    const ancien = dialogue.type === 'renommer' ? index.get(dialogue.id) : null;
    const titre =
      dialogue.type === 'renommer'
        ? `Renommer « ${ancien?.paquet.nom ?? ''} »`
        : parent
          ? `Nouveau sous-paquet dans « ${parent.paquet.nom} »`
          : 'Nouveau paquet';
    rendu = (
      <Dialogue
        titre={titre}
        {...(dialogue.type === 'creer' && !parent
          ? { texte: 'Un paquet principal porte ses propres réglages et se révise en une séance.' }
          : {})}
        actionPrincipale={{
          libelle: dialogue.type === 'renommer' ? 'Renommer' : 'Créer le paquet',
          onAction: async () => {
            const propre = validerNom();
            if (propre === null) return;
            try {
              if (dialogue.type === 'renommer') {
                await depot.renommerPaquet(dialogue.id, propre);
              } else {
                await depot.creerPaquet(propre, dialogue.parentId);
              }
              await rechargerPaquets();
              setDialogue(null);
            } catch (e) {
              setErreurNom(messageErreur(e));
            }
          },
        }}
        onFermer={() => setDialogue(null)}
      >
        <ChampTexte
          libelle={parent ? 'Nom du sous-paquet' : 'Nom du paquet'}
          valeur={nom}
          onChange={(v) => {
            setNom(v);
            setErreurNom(undefined);
          }}
          exemple={parent ? 'Chapitre 5' : 'Japonais'}
          longueurMax={200}
          {...(erreurNom ? { erreur: erreurNom } : {})}
        />
      </Dialogue>
    );
  } else if (dialogue?.type === 'supprimer') {
    const n = index.get(dialogue.id);
    const sous = n ? aplatir(n.enfants).length : 0;
    rendu = (
      <Dialogue
        titre={`Supprimer le paquet « ${n?.paquet.nom ?? ''} » ?`}
        texte={
          (dialogue.cartes === null
            ? 'Ses cartes'
            : dialogue.cartes === 0
              ? 'Il ne contient aucune carte. Il'
              : `Ses ${pluriel(dialogue.cartes, 'carte')}`) +
          (sous > 0 ? `, ses ${pluriel(sous, 'sous-paquet')}` : '') +
          (dialogue.cartes === 0
            ? ' sera supprimé.'
            : ' et leur historique de révision seront supprimés.') +
          ' Cette action est définitive.'
        }
        actionPrincipale={{
          libelle: 'Supprimer le paquet',
          variante: 'danger',
          onAction: async () => {
            await executer(() => depot.supprimerPaquet(dialogue.id), 'Paquet supprimé.');
            setDialogue(null);
            if (window.location.hash.includes(dialogue.id)) naviguer({ ecran: 'paquets' }, true);
          },
        }}
        onFermer={() => setDialogue(null)}
      />
    );
  } else if (dialogue?.type === 'ranger') {
    const n = index.get(dialogue.id);
    const cible = index.get(dialogue.parentId);
    rendu = (
      <Dialogue
        titre={`Ranger « ${n?.paquet.nom ?? ''} » dans « ${cible?.chemin.join(' › ') ?? ''} » ?`}
        texte={`Il deviendra un sous-paquet : ses réglages seront supprimés et ses cartes seront révisées dans la séance de « ${cible ? cible.chemin[0] : ''} ».`}
        actionPrincipale={{
          libelle: 'Ranger le paquet',
          onAction: async () => {
            await executer(
              () => depot.deplacerPaquet(dialogue.id, dialogue.parentId),
              `${n?.paquet.nom ?? ''} rangé dans ${cible?.paquet.nom ?? ''}.`,
            );
            setDialogue(null);
          },
        }}
        onFermer={() => setDialogue(null)}
      />
    );
  }

  return {
    noeuds,
    index,
    menuOuvert: menu?.id ?? null,
    ouvrirMenu: (id: string, ancre: Ancre) => setMenu({ id, ancre }),
    ouvrirCreation,
    /** Glisser-déposer : `dans` range dedans ; `avant` / `apres` range à côté de la cible. */
    deposer: (id: string, cibleId: string, position: 'avant' | 'apres' | 'dans') => {
      const cible = index.get(cibleId);
      if (!cible || estDescendant(index, cibleId, id)) return;
      deplacer(id, position === 'dans' ? cibleId : cible.paquet.parentId);
    },
    rendu: (
      <>
        {menu && (
          <Menu
            elements={elementsMenu(menu.id)}
            ancre={menu.ancre}
            etiquette={`Actions du paquet ${index.get(menu.id)?.paquet.nom ?? ''}`}
            onFermer={() => setMenu(null)}
          />
        )}
        {rendu}
      </>
    ),
  };
}
